import React, { createContext, useContext, useState } from 'react';
import api from '../../../seller-apis/api';
import { decrementRemainingGrabOrderNotificationCount } from '../../../utilities/grabOrderNotificationStorage';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [notificationQueue, setNotificationQueue] = useState([]);

  // Add a new notification to the queue
  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now() + Math.random(),
      isVisible: false,
      timestamp: new Date()
    };

    setNotificationQueue(prev => [...prev, newNotification]);
  };

  // Show the next notification in queue
  const showNextNotification = () => {
    if (notificationQueue.length > 0) {
      const nextNotification = notificationQueue[0];
      setNotifications(prev => [...prev, { ...nextNotification, isVisible: true }]);
      setNotificationQueue(prev => prev.slice(1));
    }
  };

  // Close a notification

  const closeNotification = (data, updateAsRead) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification?.id === data?.id
          ? { ...notification, isVisible: false }
          : notification
      )
    );

    if (updateAsRead) {
      updateRecordAsRead(data)
    }
    // Remove from array after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== data?.id));
    }, 300);
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    setNotifications([]);
    setNotificationQueue([]);
  };

  // Get notification count
  const getNotificationCount = () => {
    return notifications.length + notificationQueue.length;
  };

  // Add order lost notification (specific type)

  const addOrderLostNotification = ({ productName, winningVendor, winningPrice, vendorPrice, image, product_id, _id, SKU, foundCombination, attributes, from, urlKey, attributeValue, lowestPrice }) => {
    addNotification({
      type: 'order_lost',
      productName,
      winningVendor,
      winningPrice,
      vendorPrice,
      image,
      message: `${productName} was assigned to ${winningVendor}`,
      actionText: 'Click to adjust your price →',
      _id: _id,
      product_id: product_id,
      SKU: SKU,
      foundCombination,
      attributes,
      urlKey: from === "notification" ? urlKey : null,
      attributeValue: attributeValue,
      attributes: attributes,
      from,
      lowestPrice: Math.round(lowestPrice)
    });
  };

  // Add price change notification
  const addPriceChangeNotification = (productName, oldPrice, newPrice) => {
    addNotification({
      type: 'price_change',
      productName,
      oldPrice,
      newPrice,
      message: `Price changed for ${productName}`,
      actionText: 'View details →'
    });
  };

  // Add stock update notification
  const addStockUpdateNotification = (productName, stockStatus) => {
    addNotification({
      type: 'stock_update',
      productName,
      stockStatus,
      message: `Stock updated for ${productName}`,
      actionText: 'View product →'
    });
  };

  const updateRecordAsRead = (data) => {
    if(data?.type === 'grab_order'){
      markGrabOrderAsRead(data)
    } else {
      markOrderLostAsRead(data)
    }
  }

  const markOrderLostAsRead = (data) => {
    if (data?._id && data._id !== '') {
      api.order.markLostVendorMessagesAsRead(data._id).then((res) => {
      }).catch(err => {
        console.error('Mark as read error:', err)
      })
    } else {
      console.warn('No valid _id found');
    }
  }

  const markGrabOrderAsRead = (data) => { 
    if (data?._id && data?._id !== '') {
      api.order.markGrabOrderAsRead(data?._id).then((res) => {
        decrementRemainingGrabOrderNotificationCount();
      }).catch(err => {
        console.error('Mark as read error:', err)
      })
    } else {
      console.warn('No valid _id found');
    }
  }

  const addGrabOrderNotification = ({ productName, winningVendor, winningPrice, vendorPrice, image, product_id, _id, SKU, foundCombination, attributes, from, urlKey, attributeValue, lowestPrice, expTransferPriceFrom, expTransferPriceTo, order }) => {
    addNotification({
      type: 'grab_order',
      productName,
      winningVendor,
      winningPrice,
      vendorPrice,
      expTransferPriceFrom,
      expTransferPriceTo,
      image,
      message: `${productName} was assigned to ${winningVendor}`,
      actionText: 'Click to adjust your price →',
      _id: _id,
      product_id: product_id,
      SKU: SKU,
      foundCombination,
      attributes,
      urlKey: from === "notification" ? urlKey : null,
      attributeValue: attributeValue,
      attributes: attributes,
      from,
      order,
      lowestPrice: Math.round(lowestPrice)
    });
  };

  const value = {
    notifications,
    notificationQueue,
    addNotification,
    showNextNotification,
    closeNotification,
    clearAllNotifications,
    getNotificationCount,
    addOrderLostNotification,
    addPriceChangeNotification,
    addStockUpdateNotification,
    addGrabOrderNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
